/**
 * Small DOM helpers to keep page-entry files consistent:
 * - domReady(fn): runs fn once DOM is ready (or immediately if already ready)
 * - initOnce(el, key, fn): dataset-based idempotency guard (namespaced to avoid collisions)
 */

export function domReady(fn) {
    if (typeof fn !== 'function') return;

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
        fn();
    }
}

/**
 * Idempotent init guard using dataset.
 *
 * IMPORTANT:
 * - Namespaced to avoid colliding with component-owned dataset flags
 *   (e.g. swiper-gallery.js uses data-swiper-gallery-initialized internally).
 *
 * Example:
 *   initOnce(container, 'swiperGallery', () => { ... })
 *
 * Produces dataset key: data-page-init-swiper-gallery="true"
 */
export function initOnce(el, key, fn) {
    if (!el || typeof key !== 'string' || typeof fn !== 'function') return false;

    const normalizedKey = key
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, '-') // kebab
        .replace(/^-+|-+$/g, '')
        .toLowerCase();

    // Namespaced flag (string attribute style for easy debugging)
    const attrName = `data-page-init-${normalizedKey}`;

    if (el.hasAttribute(attrName)) return false;
    el.setAttribute(attrName, 'true');

    fn();
    return true;
}

/**
 * Escape HTML to prevent XSS attacks.
 * Uses the browser's built-in text encoding via a temporary element.
 *
 * @param {*} text - Text to escape (non-strings are converted)
 * @returns {string} HTML-escaped string
 */
export function escapeHtml(text) {
    if (text == null) return '';
    if (typeof text !== 'string') {
        text = String(text);
    }

    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}
